import { useState } from 'react';
import { HiX } from 'react-icons/hi';

const SignupDetailsModal = ({ isOpen, onClose, onSubmit, loading = false }) => {
  const [userType, setUserType] = useState(''); // 'student' or 'professional'
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [institutionName, setInstitutionName] = useState('');
  const [fieldOfStudy, setFieldOfStudy] = useState('');
  const [yearsOfExperience, setYearsOfExperience] = useState('');
  const [domainOfWork, setDomainOfWork] = useState('');

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();

    // Build user details object
    const userDetails = {
      is_student: userType === 'student',
      is_working_professional: userType === 'professional',
    };

    // Add country and city if provided
    if (country.trim()) {
      userDetails.country = country.trim();
    }
    if (city.trim()) {
      userDetails.city = city.trim();
    }

    // Add optional fields only if they have values
    if (userType === 'student') {
      if (institutionName.trim()) {
        userDetails.institution_name = institutionName.trim();
      }
      if (fieldOfStudy.trim()) {
        userDetails.field_of_study = fieldOfStudy.trim();
      }
    }

    if (userType === 'professional') {
      const yearsNum = parseInt(yearsOfExperience, 10);
      if (!isNaN(yearsNum) && yearsNum > 0) {
        userDetails.years_of_experience = yearsNum;
      }
      if (domainOfWork.trim()) {
        userDetails.domain_of_work = domainOfWork.trim();
      }
    }

    onSubmit(userDetails);
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-primary/5 to-purple-50">
          <h2 className="text-2xl font-bold text-gray-900">Tell us about yourself</h2>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 p-1 hover:bg-gray-100 rounded-full"
            aria-label="Close"
          >
            <HiX className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <p className="text-sm text-gray-600">
            Help us personalize your experience by answering a few quick questions.
          </p>

          {/* Country and City Fields */}
          <div className="space-y-4">
            <div>
              <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
                Country
              </label>
              <input
                type="text"
                id="country"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                disabled={loading}
                placeholder="e.g., United States, India, UK"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:bg-gray-100"
              />
            </div>

            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                type="text"
                id="city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                disabled={loading}
                placeholder="e.g., New York, Mumbai, London"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:bg-gray-100"
              />
            </div>
          </div>

          {/* User Type Selection - Radio Buttons */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-900">I am a...</h3>
            
            <div className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                id="is_student"
                name="user_type"
                checked={userType === 'student'}
                onChange={() => setUserType('student')}
                disabled={loading}
                className="w-5 h-5 text-primary border-gray-300 focus:ring-primary focus:ring-2 disabled:opacity-50"
              />
              <label htmlFor="is_student" className="ml-3 text-base font-medium text-gray-900 cursor-pointer select-none">
                Student
              </label>
            </div>

            <div className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors">
              <input
                type="radio"
                id="is_working_professional"
                name="user_type"
                checked={userType === 'professional'}
                onChange={() => setUserType('professional')}
                disabled={loading}
                className="w-5 h-5 text-primary border-gray-300 focus:ring-primary focus:ring-2 disabled:opacity-50"
              />
              <label htmlFor="is_working_professional" className="ml-3 text-base font-medium text-gray-900 cursor-pointer select-none">
                Working Professional
              </label>
            </div>
          </div>

          {/* Conditional Fields - Student */}
          {userType === 'student' && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">Student Details (Optional)</h3>
              
              <div>
                <label htmlFor="institution_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Institution Name
                </label>
                <input
                  type="text"
                  id="institution_name"
                  value={institutionName}
                  onChange={(e) => setInstitutionName(e.target.value)}
                  disabled={loading}
                  placeholder="e.g., MIT, Stanford University"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:bg-gray-100"
                />
              </div>

              <div>
                <label htmlFor="field_of_study" className="block text-sm font-medium text-gray-700 mb-1">
                  Field of Study
                </label>
                <input
                  type="text"
                  id="field_of_study"
                  value={fieldOfStudy}
                  onChange={(e) => setFieldOfStudy(e.target.value)}
                  disabled={loading}
                  placeholder="e.g., Computer Science, AI"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:bg-gray-100"
                />
              </div>
            </div>
          )}

          {/* Conditional Fields - Working Professional */}
          {userType === 'professional' && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">Professional Details (Optional)</h3>
              
              <div>
                <label htmlFor="years_of_experience" className="block text-sm font-medium text-gray-700 mb-1">
                  Years of Experience
                </label>
                <input
                  type="number"
                  id="years_of_experience"
                  value={yearsOfExperience}
                  onChange={(e) => setYearsOfExperience(e.target.value)}
                  disabled={loading}
                  placeholder="e.g., 5"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:bg-gray-100"
                />
              </div>

              <div>
                <label htmlFor="domain_of_work" className="block text-sm font-medium text-gray-700 mb-1">
                  Domain of Work
                </label>
                <input
                  type="text"
                  id="domain_of_work"
                  value={domainOfWork}
                  onChange={(e) => setDomainOfWork(e.target.value)}
                  disabled={loading}
                  placeholder="e.g., Software Development, Data Science"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-50 disabled:bg-gray-100"
                />
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-4 py-3 text-gray-700 bg-gray-100 rounded-lg font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-3 text-white bg-primary rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Processing...</span>
                </>
              ) : (
                <>
                  <span>Continue with Google</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignupDetailsModal;